import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { fetchArticleStats, fetchCategories } from "../services/api";
import { BarChart3, Eye, FileText, ArrowLeft, TrendingUp } from "lucide-react";

const Statistics = () => {
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [statsData, cats] = await Promise.all([
          fetchArticleStats(),
          fetchCategories(),
        ]);
        setStats(statsData);
        setCategories(cats);
      } catch (err) {
        setError("Failed to load statistics");
      }
      setLoading(false);
    };
    load();
  }, []);

  const getCat = (id) => categories.find((c) => c.id === id);

  const maxViews = stats?.articles?.length
    ? Math.max(...stats.articles.map((a) => a.views), 1)
    : 1;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#1B7A3D] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div data-testid="statistics-page" className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/"
            data-testid="stats-back-btn"
            className="p-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft size={18} className="text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Article Statistics</h1>
            <p className="text-sm text-gray-500 mt-0.5">Views and performance across all articles</p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div data-testid="stats-total-articles" className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#1B7A3D]/10 flex items-center justify-center">
                <FileText size={18} className="text-[#1B7A3D]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total_articles}</p>
                <p className="text-xs text-gray-500">Total Articles</p>
              </div>
            </div>
          </div>
          <div data-testid="stats-total-views" className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <Eye size={18} className="text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total_views.toLocaleString()}</p>
                <p className="text-xs text-gray-500">Total Views</p>
              </div>
            </div>
          </div>
          <div data-testid="stats-avg-views" className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                <TrendingUp size={18} className="text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.total_articles > 0
                    ? Math.round(stats.total_views / stats.total_articles).toLocaleString()
                    : 0}
                </p>
                <p className="text-xs text-gray-500">Avg Views / Article</p>
              </div>
            </div>
          </div>
        </div>

        {/* Articles Table */}
        {stats.articles.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
            <BarChart3 size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No articles yet. Publish your first article to see stats here.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <BarChart3 size={16} />
                Views by Article
              </h2>
            </div>
            <div className="divide-y divide-gray-50">
              {stats.articles.map((article, index) => {
                const cat = getCat(article.category);
                const barWidth = maxViews > 0 ? (article.views / maxViews) * 100 : 0;
                return (
                  <div
                    key={article.id}
                    data-testid={`stats-article-row-${index}`}
                    className="px-6 py-4 hover:bg-gray-50/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-gray-400 font-mono w-5">
                            {String(index + 1).padStart(2, "0")}
                          </span>
                          {cat && (
                            <span
                              className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                              style={{
                                color: cat.color || "#1B7A3D",
                                backgroundColor: `${cat.color || "#1B7A3D"}15`,
                              }}
                            >
                              {cat.name}
                            </span>
                          )}
                          {article.status !== "published" && (
                            <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded uppercase">
                              {article.status}
                            </span>
                          )}
                        </div>
                        <Link
                          to={`/article/${article.id}`}
                          className="text-sm font-semibold text-gray-900 hover:text-[#1B7A3D] transition-colors line-clamp-1"
                        >
                          {article.title}
                        </Link>
                        {article.author_name && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            by {article.author_name} {article.date ? `· ${article.date}` : ""}
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <p className="text-lg font-bold text-gray-900">{article.views.toLocaleString()}</p>
                        <p className="text-[10px] text-gray-400">views</p>
                      </div>
                    </div>
                    {/* Bar */}
                    <div className="ml-7 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${barWidth}%`,
                          backgroundColor: cat?.color || "#1B7A3D",
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Statistics;
